import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, tap, catchError, throwError } from 'rxjs';
import { environment } from 'src/env/environments';
import { GoogleLoginRequest, LoginRequest, LoginResponse, RegisterOperatorRequest, RoleInfo, SignUpRequest } from '../models/auth.model';
import { SlugService } from '../services/slug.service';
import { PwaDetectionService } from '../services/pwaDetection.service';
import { ApiResponse } from '../models/app.model';
import '../types/google.types';
import { Router } from '@angular/router';
import { PermissionService } from '../services/permission.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private googleInitialized = false;
  private menuItemsSubject = new BehaviorSubject<string[]>([]);
  menuItems$ = this.menuItemsSubject.asObservable();
  private isLoggedInSubject = new BehaviorSubject<boolean>(this.isLoggedIn());

  constructor(
    private http: HttpClient,
    private slugService: SlugService,
    private pwaDetectionService: PwaDetectionService,
    private router: Router,
    private permissionService: PermissionService
  ) {}

  private buildUrl(endpoint: string): string {
    const slug = this.slugService.getSlug();
    if (!slug) throw new Error('Slug non trovato');
    return `${environment.apiUrl}/${slug}/auth/${endpoint}`;
  }

  public login(request: LoginRequest): Observable<ApiResponse<LoginResponse>> {
    return this.http.post<ApiResponse<LoginResponse>>(this.buildUrl('login'), request).pipe(
      tap(res => {
        if (this.saveTokens(res.data.token, res.data.refreshToken)) {
          this.isLoggedInSubject.next(true);
          this.permissionService.setPermissions(res.data.permissions);

        }
      })
    )
  }

  public register(request: SignUpRequest): Observable<ApiResponse<string>> {
    return this.http.post<ApiResponse<string>>(this.buildUrl('register'), request);
  }

  public registerOperator(request: RegisterOperatorRequest): Observable<ApiResponse<string>> {
    return this.http.post<ApiResponse<string>>(this.buildUrl('register-operator'), request);
  }

  public getOperatorEmailByToken(token: string): Observable<ApiResponse<{ email: string }>> {
    return this.http.get<ApiResponse<{ email: string }>>(this.buildUrl(`operator-email?token=${encodeURIComponent(token)}`));
  }

  public isLoggedIn(): boolean {
    return !!localStorage.getItem('token');
  }

  public isLoggedIn$(): Observable<boolean> {
    return this.isLoggedInSubject.asObservable();
  }

  public saveToken(token: string): boolean {
    if (this.pwaDetectionService.isAccessAllowed()) {
      localStorage.setItem('token', token);
      return true;
    }
    return false;
  }

  public saveTokens(token: string, refreshToken: string): boolean {
    if (this.pwaDetectionService.isAccessAllowed()) {
      localStorage.setItem('token', token);
      localStorage.setItem('refreshToken', refreshToken);
      return true;
    }
    return false;
  }

  public logout(): void {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    this.isLoggedInSubject.next(false);
    this.permissionService.clearPermissions()
    this.router.navigate(['/auth']);
  }

  public getToken(): string | null {
    if (!this.pwaDetectionService.isAccessAllowed()) {
      return null;
    }
    return localStorage.getItem('token');
  }

  public getRefreshToken(): string | null {
    if (!this.pwaDetectionService.isAccessAllowed()) {
      return null;
    }
    return localStorage.getItem('refreshToken');
  }

  public refreshAccessToken(): Observable<ApiResponse<LoginResponse>> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      throw new Error('Refresh token non trovato');
    }
    return this.http.post<ApiResponse<LoginResponse>>(this.buildUrl('refresh-token'), { refreshToken }).pipe(
      tap(res => {
        if (this.saveTokens(res.data.token, res.data.refreshToken)) {
          this.isLoggedInSubject.next(true);
          this.permissionService.setPermissions(res.data.permissions);
        }
      })
    );
  }

  public googleLogin(idToken: string): Observable<ApiResponse<LoginResponse>> {
    const request: GoogleLoginRequest = { idToken };
    return this.http.post<ApiResponse<LoginResponse>>(this.buildUrl('google-login'), request);
  }

  private redirectToRouteBasedOnRole(role: RoleInfo){
    switch (role) {
    case RoleInfo.Admin:
    case RoleInfo.Operator:
        this.router.navigateByUrl('/admin/profile');
        break;
      case RoleInfo.Customer:
      this.router.navigateByUrl('/book');
      break;
      default:
        break;
    }
  }

//#region Google Auth
  public initializeGoogleSignIn(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.googleInitialized) {
        resolve();
        return;
      }

      if (typeof window !== 'undefined' && window.google) {
        window.google.accounts.id.initialize({
          client_id: environment.googleClientId,
          callback: (response: any) => {
            this.handleGoogleSignIn(response);
          },
          auto_select: false,
          cancel_on_tap_outside: false,
          use_fedcm_for_prompt: false,
        });
        this.googleInitialized = true;
        resolve();
      } else {
        this.isLoggedInSubject.next(false);
        reject(new Error('Google Sign-In library not loaded'));
      }
    });
  }

  private handleGoogleSignIn(response: any): void {
    if (!response.credential) {
      return;
    }

    this.googleLogin(response.credential).subscribe({
      next: (res) => {
        if (this.saveTokens(res.data.token, res.data.refreshToken)) {
          this.isLoggedInSubject.next(true);
          this.permissionService.setPermissions(res.data.permissions);
          this.redirectToRouteBasedOnRole(res.data.user.role);
        } else {
          this.router.navigateByUrl('/auth');
        }
      },
      error: (err) => {
        alert(`Errore doogle: ${err.error?.message || err.message}`);
      }
    });
  }


  public renderGoogleButton(elementId: string): void {
    if (typeof window === 'undefined') {
      return;
    }

    if (!window.google?.accounts?.id) {
      return;
    }

    if (!this.googleInitialized) {
      this.initializeGoogleSignIn().then(() => {
        this.renderButtonInternal(elementId);
      }).catch(err => {
      });
      return;
    }

    this.renderButtonInternal(elementId);
  }

  private renderButtonInternal(elementId: string): void {
    const element = document.getElementById(elementId);

    if (!element) {
      return;
    }
    const w = window as Window & typeof globalThis & { google?: any };
    if (!w.google?.accounts?.id) {
      return;
    }
    element.innerHTML = '';

    try {
      w.google.accounts.id.renderButton(
        element,
        {
          theme: 'outline',
          size: 'large',
          type: 'standard',
          text: 'signin_with',
          width: element.offsetWidth || 400,
          locale: 'it'
        }
      );
      setTimeout(() => {
        element.style.maxWidth = 'none';
        element.style.width = '100%';

        const firstChild = element.firstElementChild;
        if (firstChild instanceof HTMLElement) {
          firstChild.style.maxWidth = 'none';
          firstChild.style.width = '100%';
        }

        const buttonDiv = element.querySelector('.nsm7Bb-HzV7m-LgbsSe');
        if (buttonDiv instanceof HTMLElement) {
          buttonDiv.style.maxWidth = 'none';
          buttonDiv.style.width = '100%';
        }
      }, 0);

    } catch (error) {
      console.error('Error rendering button:', error);
    }
  }

//#endregion
}
